import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { complianceApi, RegulatoryReport } from '../../api/compliance';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Table } from '../../components/ui/Table';
import { Drawer } from '../../components/ui/Drawer';

/** Statutory clocks, in hours from when suspicion was formed. */
const DEADLINE_HOURS: Record<string, number> = { str: 24, ctr: 168, ftr: 168 };

function deadline(report: RegulatoryReport) {
  const hours = DEADLINE_HOURS[report.reportType] ?? 168;
  const due = new Date(report.detectedAt).getTime() + hours * 3_600_000;
  const ms = due - Date.now();
  const h = Math.floor(Math.abs(ms) / 3_600_000);
  const label = ms <= 0
    ? (h < 24 ? `${h}h overdue` : `${Math.floor(h / 24)}d overdue`)
    : (h < 24 ? `${h}h left` : `${Math.floor(h / 24)}d left`);
  return { label, overdue: ms <= 0, urgent: ms > 0 && ms < 6 * 3_600_000 };
}

/**
 * Regulatory reports awaiting action.
 *
 * ## The deadline is the screen
 *
 * An STR must reach the NFIU within 24 hours of the suspicion being formed,
 * and lateness costs ₦1,000,000 per day. The clock runs from `detectedAt` —
 * when suspicion arose — not from when the draft was written or opened here,
 * so time can already be short when a report first appears.
 *
 * ## Escalated is not discharged
 *
 * Under the sponsored model the partner bank files, so the obligation is only
 * demonstrably met once they acknowledge. A report sitting in `escalated` with
 * no acknowledgement is an open exposure wearing the costume of a completed
 * task, which is why the two states are shown separately rather than merged
 * into one "sent" status.
 *
 * ## Narratives are internal
 *
 * The narrative states the grounds for suspicion in plain terms — the exact
 * inverse of customer-facing copy. None of it may be surfaced to a customer
 * (MLPPA s.14).
 */
export const RegulatoryReports: React.FC = () => {
  usePage('Regulatory Reports');
  const { can } = useAuth();
  const toast = useToast();

  const [overdue, setOverdue] = useState<RegulatoryReport[]>([]);
  const [unacknowledged, setUnacknowledged] = useState<RegulatoryReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RegulatoryReport | null>(null);
  const [partnerRef, setPartnerRef] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await complianceApi.outstandingReports();
      setOverdue(data.overdue);
      setUnacknowledged(data.unacknowledged);
    } catch {
      toast.error('Could not load outstanding reports.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function escalate() {
    if (!selected) return;
    setBusy(true);
    try {
      await complianceApi.escalateReport(selected.id, partnerRef.trim() || undefined);
      toast.success(`${selected.reference} marked as escalated.`);
      setSelected(null);
      setPartnerRef('');
      void load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Could not record escalation.';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  async function acknowledge() {
    if (!selected || !partnerRef.trim()) return;
    setBusy(true);
    try {
      await complianceApi.acknowledgeReport(selected.id, partnerRef.trim());
      toast.success('Acknowledgement recorded.');
      setSelected(null);
      setPartnerRef('');
      void load();
    } catch {
      toast.error('Could not record the acknowledgement.');
    } finally {
      setBusy(false);
    }
  }

  const columns = [
    { key: 'reference', header: 'Reference', render: (r: RegulatoryReport) => <span className="font-mono text-xs">{r.reference}</span> },
    { key: 'type', header: 'Type', render: (r: RegulatoryReport) => r.reportType.toUpperCase() },
    { key: 'amount', header: 'Amount', render: (r: RegulatoryReport) => `₦${Number(r.amount).toLocaleString()}` },
    {
      key: 'deadline',
      header: 'Statutory deadline',
      render: (r: RegulatoryReport) => {
        const d = deadline(r);
        return (
          <span className={d.overdue ? 'font-semibold text-red-600' : d.urgent ? 'font-semibold text-amber-600' : ''}>
            {d.label}
          </span>
        );
      },
    },
    { key: 'status', header: 'Status', render: (r: RegulatoryReport) => <Badge variant="neutral">{r.status}</Badge> },
  ];

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="mb-2 text-sm font-semibold">
          Awaiting escalation to the partner institution
        </h2>
        <p className="mb-3 text-xs text-gray-500">
          The statutory clock runs from when the suspicion was formed, not from
          when the report was drafted.
        </p>
        <Table
          data={overdue}
          emptyMessage="Nothing outstanding."
          onRowClick={(r) => { setSelected(r); setPartnerRef(''); }}
          columns={columns}
        />
      </Card>

      <Card>
        <h2 className="mb-2 text-sm font-semibold">
          Escalated, not yet acknowledged
        </h2>
        <p className="mb-3 text-xs text-gray-500">
          The obligation is not demonstrably discharged until the partner
          confirms receipt with their own reference.
        </p>
        <Table
          data={unacknowledged}
          emptyMessage="Nothing awaiting acknowledgement."
          onRowClick={(r) => { setSelected(r); setPartnerRef(r.partnerRef ?? ''); }}
          columns={columns}
        />
      </Card>

      <Drawer isOpen={!!selected} onClose={() => setSelected(null)} title={selected?.reference ?? ''}>
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="flex gap-2">
              <Badge variant="neutral">{selected.reportType.toUpperCase()}</Badge>
              <Badge variant={deadline(selected).overdue ? 'danger' : 'info'}>
                {deadline(selected).label}
              </Badge>
            </div>

            <div>
              <div className="text-xs text-gray-500">
                Narrative — internal only, never shown to the customer
              </div>
              <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs">
                {selected.narrative ?? 'No narrative yet. This must be written before escalation.'}
              </pre>
            </div>

            {can('compliance.review') && (
              <div className="space-y-2 border-t pt-3">
                <label className="block text-xs font-semibold uppercase text-gray-500">
                  Partner reference
                </label>
                <input
                  className="w-full rounded border px-2 py-1"
                  placeholder="The partner's own reference for this filing"
                  value={partnerRef}
                  onChange={(e) => setPartnerRef(e.target.value)}
                />

                {!selected.escalatedToPartner ? (
                  <>
                    <Button disabled={busy || !selected.narrative?.trim()} onClick={escalate}>
                      {busy ? 'Recording…' : 'Mark as escalated'}
                    </Button>
                    {!selected.narrative?.trim() && (
                      <p className="text-xs text-amber-700">
                        A narrative is required first — the backend refuses to
                        escalate a report without one.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Button disabled={busy || !partnerRef.trim()} onClick={acknowledge}>
                      {busy ? 'Recording…' : 'Record acknowledgement'}
                    </Button>
                    <p className="text-xs text-gray-500">
                      A reference is required: “they confirmed on a call” is not
                      something an examiner can follow.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};
